import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Check, CheckCheck, X, MailOpen, Filter, ExternalLink, AlertTriangle } from "lucide-react";
import { formatDateTime } from "@/lib/format";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22" };

const PRIORITY = {
  urgent: { label: "Urgent", bg: "#FDECEC", fg: "#9C1E22" },
  high: { label: "High", bg: "#FEF3C7", fg: "#92400E" },
  normal: { label: "Normal", bg: "#EEF2F5", fg: "#4B5563" },
  low: { label: "Low", bg: "#F1F5F9", fg: "#64748B" },
};
const TYPE_LABELS = {
  new_lead: "New lead", live_chat: "Live chat", chat_handoff: "Live chat escalation",
  new_chat: "New chat", task_due: "Task due", follow_up_due: "Follow-up due",
  document_needs_review: "Document review", document: "Document", client_document: "Client document",
  consultation_requested: "Consultation", partner_update: "Partner update",
  urgent_escalation: "Escalation", meeting: "Meeting", external_referral: "External referral",
};
const prettyType = (t) => TYPE_LABELS[t] || (t || "Notification").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function NotificationList({ variant = "staff" }) {
  const navigate = useNavigate();
  const isStaff = variant === "staff";
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({});
  const [fType, setFType] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [fWindow, setFWindow] = useState("all");

  const load = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (fType !== "all") params.ntype = fType;
      if (fPriority !== "all") params.priority = fPriority;
      if (unreadOnly) params.unread_only = true;
      const { data } = await api.get("/notifications/center", { params });
      setItems(data.items || []);
      setSelected({});
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [fType, fPriority, unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const typeOptions = useMemo(() => {
    const set = new Set((items || []).map((n) => n.type).filter(Boolean));
    return Array.from(set);
  }, [items]);

  const visible = useMemo(() => {
    if (fWindow === "all") return items || [];
    const now = Date.now();
    const cutoff = fWindow === "today" ? 24 * 3600e3 : 7 * 24 * 3600e3;
    return (items || []).filter((n) => n.created_at && now - new Date(n.created_at).getTime() <= cutoff);
  }, [items, fWindow]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = visible.length > 0 && selectedIds.length === visible.length;

  const markRead = async (ids) => {
    try { await api.post("/notifications/mark-read", { ids }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const markUnread = async (ids) => {
    try { await api.post("/notifications/mark-unread", { ids }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const markAllRead = async () => {
    try { const { data } = await api.post("/notifications/mark-read", { all: true }); toast.success(`Marked ${data.updated} as read.`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dismiss = async (id) => {
    try { await api.post(`/notifications/${id}/dismiss`); setItems((x) => (x || []).filter((n) => n.id !== id)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openItem = async (n) => {
    if (!n.read) { try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch (e) { /* noop */ } }
    const link = n.related_url || n.link;
    if (isStaff && link) navigate(link);
    else load();
  };

  if (items === null) return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;

  return (
    <div data-testid="notification-list">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {isStaff && (
          <>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger className="h-8 w-[160px] text-[13px]" data-testid="notif-filter-type"><Filter size={13} className="mr-1" /><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {typeOptions.map((t) => <SelectItem key={t} value={t}>{prettyType(t)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fPriority} onValueChange={setFPriority}>
              <SelectTrigger className="h-8 w-[140px] text-[13px]" data-testid="notif-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <Select value={fWindow} onValueChange={setFWindow}>
          <SelectTrigger className="h-8 w-[130px] text-[13px]" data-testid="notif-filter-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="today">Last 24 hours</SelectItem>
            <SelectItem value="week">Last 7 days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={unreadOnly ? "default" : "outline"} size="sm" onClick={() => setUnreadOnly((v) => !v)}
          data-testid="notif-filter-unread" className={`h-8 gap-1 ${unreadOnly ? "bg-[#1C4358] text-white hover:bg-[#16374a]" : "border-[#D6E0E7] text-[#1C4358]"}`}>
          <MailOpen size={14} /> Unread only
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {isStaff && selectedIds.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => markRead(selectedIds)} data-testid="notif-bulk-read" className="h-8 gap-1 border-[#D6E0E7] text-[#1C4358]"><Check size={14} /> Read ({selectedIds.length})</Button>
              <Button variant="outline" size="sm" onClick={() => markUnread(selectedIds)} data-testid="notif-bulk-unread" className="h-8 border-[#D6E0E7] text-[#1C4358]">Unread</Button>
              <Button variant="outline" size="sm" onClick={async () => { for (const id of selectedIds) await api.post(`/notifications/${id}/dismiss`); load(); }} data-testid="notif-bulk-dismiss" className="h-8 gap-1 border-[#D6E0E7] text-[#9C1E22]"><X size={14} /> Dismiss</Button>
            </>
          )}
          <Button size="sm" onClick={markAllRead} data-testid="notif-mark-all-read" className="h-8 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white"><CheckCheck size={14} /> Mark all read</Button>
        </div>
      </div>

      {isStaff && visible.length > 0 && (
        <label className="flex items-center gap-2 px-1 pb-2 text-[12px]" style={{ color: C.slate }}>
          <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? Object.fromEntries(visible.map((n) => [n.id, true])) : {})} data-testid="notif-select-all" />
          Select all shown
        </label>
      )}

      {visible.length === 0 ? (
        <div className="text-center py-16 rounded-xl border" style={{ borderColor: C.line }} data-testid="notif-empty">
          <Bell size={28} className="mx-auto mb-2 text-[#9BB9CE]" />
          <p className="text-sm" style={{ color: C.slate }}>You're all caught up.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((n) => {
            const p = PRIORITY[n.priority] || PRIORITY.normal;
            const link = n.related_url || n.link;
            return (
              <div key={n.id} data-testid="notif-row"
                className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${!n.read ? "bg-[rgba(155,185,206,0.08)]" : "bg-white"}`}
                style={{ borderColor: n.read ? C.line : "#CDDDE8" }}>
                {isStaff && (
                  <Checkbox className="mt-1" checked={!!selected[n.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [n.id]: v }))} data-testid="notif-row-select" />
                )}
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: C.red }} data-testid="notif-unread-dot" />}
                <button onClick={() => openItem(n)} className="min-w-0 flex-1 text-left" data-testid="notif-open">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13.5px] font-semibold" style={{ color: C.charcoal }}>{n.title}</span>
                    <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: p.bg, color: p.fg }} data-testid="notif-priority-badge">{p.label}</span>
                    <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: "#F1F5F9", color: C.slate }}>{prettyType(n.type)}</span>
                    {n.escalated && (
                      <span className="text-[10.5px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: "#FDECEC", color: C.red }} data-testid="notif-escalated-badge">
                        <AlertTriangle size={10} /> Escalated{n.escalation_tier ? ` · ${String(n.escalation_tier).replace(/_/g, " ")}` : ""}
                      </span>
                    )}
                  </div>
                  {(n.body || n.message) && <div className="text-[12.5px] mt-0.5" style={{ color: C.slate }}>{n.body || n.message}</div>}
                  {n.escalated && n.origin_created_at && (
                    <div className="text-[11px] mt-0.5" style={{ color: C.red }} data-testid="notif-escalation-detail">
                      {n.escalation_reason || "SLA breach"} · originally created {formatDateTime(n.origin_created_at)}
                    </div>
                  )}
                  <div className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: "#8A97A3" }}>
                    {n.escalated ? "Escalated " : ""}{formatDateTime(n.created_at)}
                    {isStaff && link && <span className="inline-flex items-center gap-0.5 text-[#1C4358]"><ExternalLink size={11} /> Open</span>}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {n.read ? (
                    <Button variant="ghost" size="sm" onClick={() => markUnread([n.id])} data-testid="notif-row-unread" className="h-7 px-2 text-[12px] text-[#4B5563]">Unread</Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => markRead([n.id])} data-testid="notif-row-read" className="h-7 px-2 text-[12px] text-[#1C4358] gap-1"><Check size={13} /> Read</Button>
                  )}
                  <button onClick={() => dismiss(n.id)} data-testid="notif-row-dismiss" title="Dismiss" className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[rgba(156,30,34,0.08)] text-[#9C1E22]"><X size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
